import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';

/**
 * Lightweight rate pill row that shows USD/LYD and EUR/LYD from the same
 * backend the mobile app uses (`GET /v1/rates/latest`). Re-polls every
 * minute. On failure it shows nothing — the hero already stands on its
 * own without it.
 */
type RateRow = {
  currency: string;
  cashRate?: number;
  previousCashRate?: number | null;
  rate?: number;
  previousRate?: number | null;
};

type LatestResponse = {
  data: RateRow[];
  fetchedAt: string;
};

const TICKER_CODES = ['USD', 'EUR'] as const;
const REFRESH_MS = 60_000;
const fmt = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

export default function LiveRatesTicker() {
  const [rates, setRates] = useState<RateRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`/v1/rates/latest?currencies=${TICKER_CODES.join(',')}`, {
          headers: { accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: LatestResponse = await res.json();
        if (cancelled) return;
        setRates(json.data);
        setError(false);
      } catch {
        if (cancelled) return;
        setError(true);
      }
    }

    void load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (error) return null;

  return (
    <div className="glass-card flex flex-wrap items-center gap-4 px-4 py-3 sm:gap-6">
      <span className="pill">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
        Live LYD rates
      </span>
      {rates === null ? (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          {TICKER_CODES.map((code) => {
            const row = rates.find((r) => r.currency === code);
            if (!row) return null;
            const current = row.cashRate ?? row.rate;
            const previous = row.previousCashRate ?? row.previousRate ?? null;
            if (current == null) return null;
            const delta = previous != null ? current - previous : 0;
            const pct = previous && previous !== 0 ? (delta / previous) * 100 : 0;
            const up = delta >= 0;
            return (
              <div key={code} className="flex items-baseline gap-2">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-400">
                  {code}/LYD
                </span>
                <span className="font-display text-lg font-bold text-white tabular-nums">
                  {fmt.format(current)}
                </span>
                {previous != null && (
                  <span
                    className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                      up ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {up ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    )}
                    {Math.abs(pct).toFixed(2)}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
